import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const InterviewFeedback = ({ interviews, onAddFeedback }) => {
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [feedbackForm, setFeedbackForm] = useState({
    rating: '',
    technicalSkills: '',
    communication: '',
    culturalFit: '',
    strengths: '',
    weaknesses: '',
    recommendation: '',
    notes: ''
  });

  const getStatusColor = (status) => {
    const colors = {
      'Scheduled': 'bg-blue-100 text-blue-800',
      'Completed': 'bg-green-100 text-green-800',
      'Cancelled': 'bg-red-100 text-red-800',
      'Rescheduled': 'bg-yellow-100 text-yellow-800'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800';
  };

  const handleSubmitFeedback = (e) => {
    e?.preventDefault();
    onAddFeedback(selectedInterview?.id, feedbackForm);
    setSelectedInterview(null);
    setFeedbackForm({
      rating: '',
      technicalSkills: '',
      communication: '',
      culturalFit: '',
      strengths: '',
      weaknesses: '',
      recommendation: '',
      notes: ''
    });
  };

  const handleInputChange = (field, value) => {
    setFeedbackForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="space-y-6">
      {/* Interview List */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground">Interview History</h3>
        
        {interviews?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h4 className="text-lg font-medium text-foreground mb-2">No Interviews Scheduled</h4>
            <p className="text-muted-foreground">Schedule an interview to start collecting feedback.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {interviews?.map((interview) => (
              <div key={interview?.id} className="border border-border rounded-lg p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="font-medium text-foreground">{interview?.type}</h4>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(interview?.status)}`}>
                        {interview?.status}
                      </span>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <Icon name="Calendar" size={14} />
                        <span>{interview?.date}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Icon name="Clock" size={14} />
                        <span>{interview?.time}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Icon name="User" size={14} />
                        <span>{interview?.interviewer}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    {interview?.status === 'Completed' && !interview?.feedback && (
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="MessageSquare"
                        iconPosition="left"
                        onClick={() => setSelectedInterview(interview)}
                      >
                        Add Feedback
                      </Button>
                    )}
                    {interview?.feedback && (
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Eye"
                        iconPosition="left"
                      >
                        View Feedback
                      </Button>
                    )}
                  </div>
                </div>

                {/* Existing Feedback Display */}
                {interview?.feedback && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <span className="text-sm text-muted-foreground">Overall Rating:</span>
                        <div className="flex items-center gap-1 mt-1">
                          {[...Array(5)]?.map((_, i) => (
                            <Icon
                              key={i}
                              name="Star"
                              size={16}
                              className={i < interview?.feedback?.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}
                            />
                          ))}
                          <span className="text-sm font-medium ml-2">{interview?.feedback?.rating}/5</span>
                        </div>
                      </div>
                      <div>
                        <span className="text-sm text-muted-foreground">Recommendation:</span>
                        <p className="font-medium text-foreground mt-1">{interview?.feedback?.recommendation}</p>
                      </div>
                      <div>
                        <span className="text-sm text-muted-foreground">Feedback By:</span>
                        <p className="font-medium text-foreground mt-1">{interview?.feedback?.reviewer}</p>
                      </div>
                    </div>
                    
                    {interview?.feedback?.notes && (
                      <div>
                        <span className="text-sm text-muted-foreground">Notes:</span>
                        <p className="text-sm text-foreground mt-1">{interview?.feedback?.notes}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Feedback Form Modal */}
      {selectedInterview && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg shadow-modal w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-foreground">
                  Interview Feedback - {selectedInterview?.type}
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="X"
                  onClick={() => setSelectedInterview(null)}
                />
              </div>
            </div>

            <form onSubmit={handleSubmitFeedback} className="p-6 space-y-6">
              {/* Rating */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Overall Rating *
                </label>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5]?.map((rating) => (
                    <button
                      key={rating}
                      type="button"
                      onClick={() => handleInputChange('rating', rating)}
                      className="p-1"
                    >
                      <Icon
                        name="Star"
                        size={24}
                        className={
                          rating <= feedbackForm?.rating
                            ? 'text-yellow-400 fill-current' :'text-gray-300 hover:text-yellow-200'
                        }
                      />
                    </button>
                  ))}
                  <span className="ml-2 text-sm text-muted-foreground">
                    {feedbackForm?.rating ? `${feedbackForm?.rating}/5` : 'Select rating'}
                  </span>
                </div>
              </div>

              {/* Assessment Areas */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  label="Technical Skills"
                  type="text"
                  placeholder="Rate 1-10"
                  value={feedbackForm?.technicalSkills}
                  onChange={(e) => handleInputChange('technicalSkills', e?.target?.value)}
                />
                <Input
                  label="Communication"
                  type="text"
                  placeholder="Rate 1-10"
                  value={feedbackForm?.communication}
                  onChange={(e) => handleInputChange('communication', e?.target?.value)}
                />
                <Input
                  label="Cultural Fit"
                  type="text"
                  placeholder="Rate 1-10"
                  value={feedbackForm?.culturalFit}
                  onChange={(e) => handleInputChange('culturalFit', e?.target?.value)}
                />
              </div>

              {/* Text Areas */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Key Strengths
                  </label>
                  <textarea
                    rows={4}
                    className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
                    placeholder="What are the candidate's main strengths?"
                    value={feedbackForm?.strengths}
                    onChange={(e) => handleInputChange('strengths', e?.target?.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Areas for Improvement
                  </label>
                  <textarea
                    rows={4}
                    className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
                    placeholder="What areas need improvement?"
                    value={feedbackForm?.weaknesses}
                    onChange={(e) => handleInputChange('weaknesses', e?.target?.value)}
                  />
                </div>
              </div>

              {/* Recommendation */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Recommendation *
                </label>
                <select
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                  value={feedbackForm?.recommendation}
                  onChange={(e) => handleInputChange('recommendation', e?.target?.value)}
                  required
                >
                  <option value="">Select recommendation</option>
                  <option value="Strong Hire">Strong Hire</option>
                  <option value="Hire">Hire</option>
                  <option value="No Hire">No Hire</option>
                  <option value="Strong No Hire">Strong No Hire</option>
                </select>
              </div>

              {/* Additional Notes */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Additional Notes
                </label>
                <textarea
                  rows={4}
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
                  placeholder="Any additional comments or observations..."
                  value={feedbackForm?.notes}
                  onChange={(e) => handleInputChange('notes', e?.target?.value)}
                />
              </div>

              {/* Form Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-border">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setSelectedInterview(null)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  disabled={!feedbackForm?.rating || !feedbackForm?.recommendation}
                >
                  Submit Feedback
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterviewFeedback;